import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { StatsGrid, type StatTile } from "@/components/ui/stats-grid";
import { formatDate } from "@/lib/format-date";
import { useAdminOrgs } from "@/hooks/queries/useAdminOrgs";
import { useAdminOrgTotals } from "@/hooks/queries/useAdminOrgTotals";
import type { AdminOrgTotals } from "@/hooks/queries/admin-org-totals-types";
import type { AdminOrgsFilters } from "@/hooks/queries/admin-orgs-types";
import { toAdminOrgError } from "@/routes/admin/admin-orgs-error";
import {
  EMPTY_CELL,
  orgCalibrationPill,
  orgStatusPills,
} from "@/routes/admin/admin-orgs-derivations";

// Screen 23 — the platform organization directory (Story 15.6, FR59/FR74/FR78).
//
// SIX COLUMNS, not the mockup's seven: Organization / Seats · used·capacity /
// Org admins / Calibration / Next renewal / Status. Region is DROPPED — column
// and filter both — on the client answer of 2026-08-25; region is inferred from
// the full organization name, which is why the name has to be typed correctly.
//
// ONE SEARCH FIELD, matching `name` AND `external_reference_id` server-side. The
// CRM key shares the box with the name on purpose: after a create whose 201 was
// lost in transit, the key is the only thing the operator still holds (DW-320 /
// IF-1), and searching it here is the documented recovery. That is also why the
// Organization cell prints the key UNDER the name — an operator who cannot SEE
// the matched key cannot tell a match from a coincidence.
//
// THE FOUR PLATFORM STAT TILES SHIP HERE NOW (Story 15.7 AC13). 15.6 deferred
// them by name and asserted their absence; they are read from
// `GET /admin/orgs/totals` (`useAdminOrgTotals`), not from this screen's own list
// response, because they are estate-wide aggregates rather than a property of
// the page being listed — and because bolting a totals block onto the paginated
// envelope would fork it for one screen.
//
// That read is this screen's OWN and is deliberately not the console's. Mounting
// `useAdminConsole` here — which is what shipped first — wrote a
// `super_admin.console.viewed` audit row every time somebody opened the
// directory, into an append-only store kept seven years, and paid for a
// platform-wide entitlement scan for tiles this screen does not render (DW-423).
//
// TWO SURFACES REMAIN ABSENT ON PURPOSE (AC30, contract B-4), and the story
// asserts their absence rather than describing it:
//   • the "Subscription health" filter      → it rests on a predicate the client
//                                             has not answered
//   • the Region column and the Region filter
//
// The "Stripe + seats" info banner is also absent here, and that is now a
// statement about THIS surface rather than about the product: it explains a hard
// stop this screen has no control for, and the sentence ships where the operator
// acts on it — Screen 24's Contract card, beside the seat-capacity control
// (`ContractCard.tsx`, DW-386).
//
// The mockup's admin-scope block is not rendered by this screen and never will
// be: on Screen 23 it sits INSIDE the same `<aside class="sidebar">` as the
// `nav-group` holding "Directory", so the shell that owns that landmark is where
// it belongs. Story 15.7 built it there (`SidebarNav`), which is also what the
// mobile drawer renders — one insertion point, both breakpoints. DW-401's
// false justification is discharged.

const HEADING_ID = "admin-orgs-heading";
const LIST_HEADING_ID = "admin-orgs-list-heading";

/**
 * Same window as the sibling admin directory (`AdminUsersListScreen.tsx`
 * `USER_SEARCH_DEBOUNCE_MS`) so the two admin search boxes behave identically.
 */
export const ORG_SEARCH_DEBOUNCE_MS = 300;

/**
 * Screen 23's four platform stat tiles (Story 15.7 AC13), by the labels the
 * client-validated mockup prints.
 *
 * They are ESTATE-WIDE aggregates and are deliberately unaffected by the search
 * box: they answer "how big is the platform", not "how big is this result page".
 * `organizations` therefore equals the directory's own `count` only on the
 * UNFILTERED list — which is the AC12 pin, and the reason both sides scope on
 * `org_type = 'professional'`.
 *
 * They come from `GET /admin/orgs/totals`, this screen's OWN read. They used to
 * ride the console payload, which is what made this directory call
 * `GET /admin/console` and write a `super_admin.console.viewed` audit row for a
 * console nobody opened (DW-423).
 */
export function platformStats(data: AdminOrgTotals): StatTile[] {
  return [
    { label: "Organizations", value: data.organizations },
    { label: "Purchased seats", value: data.purchased_seats },
    { label: "Assigned seats", value: data.assigned_seats },
    {
      // The window is read off the response, never written into this copy: the
      // label and the count it sits over cannot then disagree.
      label: `Org renewals (${data.renewal_window_days}d)`,
      value: data.renewals_in_window,
    },
  ];
}

/**
 * The date the per-row contract bands measure their window from — the SERVER's,
 * when the totals read has arrived.
 *
 * Falls back to the browser's own clock when it has not. That fallback is the
 * pre-15.7 behaviour and no worse than it: the bands are then anchored to local
 * midnight, which is exactly the drift `today` exists to close, but a directory
 * that refuses to render its rows because a summary read failed would be a worse
 * answer than a row dated a few hours off.
 *
 * BOTH contract bands move with it, not only the renewal one DW-426 was raised
 * about. `orgContractBand` reads this same `now` for its `days < 0` branch too,
 * so an operator far enough east of UTC can see a contract that ended on their
 * local yesterday still read "Renewal soon" rather than "Contract expired" for
 * the hours the two dates disagree. That is the intended direction — the row
 * then says what the server would say, and the alternative is a row and a tile
 * disagreeing about the same organization — but it is a second behaviour change
 * riding one anchor and is written down rather than discovered.
 */
export function bandAnchor(totals: AdminOrgTotals | undefined): Date | undefined {
  if (!totals) return undefined;
  // `T00:00:00` with no zone suffix parses as LOCAL midnight, which is the frame
  // `daysUntil` normalises both of its ends into. A bare `new Date("2026-01-15")`
  // would parse as UTC midnight and reintroduce the off-by-one for every viewer
  // west of UTC — the `lib/format-date.ts` lesson.
  const parsed = new Date(`${totals.today}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** Read the URL search params into the filter object the query key is built from. */
export function orgFiltersFromParams(params: URLSearchParams): AdminOrgsFilters {
  const filters: AdminOrgsFilters = {};
  const q = params.get("q");
  const page = Number(params.get("page"));

  if (q) filters.q = q;
  if (Number.isInteger(page) && page > 1) filters.page = page;
  return filters;
}

export function OrgDirectoryScreen() {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = React.useMemo(() => orgFiltersFromParams(searchParams), [searchParams]);

  // The search box is uncontrolled by the URL between keystrokes: typing updates
  // local state immediately and the URL (and therefore the request) only after the
  // debounce, so a search does not fire one request per character.
  const [searchTerm, setSearchTerm] = React.useState(filters.q ?? "");
  const urlQ = filters.q ?? "";
  // Re-seed only when the URL changes from OUTSIDE this input (a back-navigation,
  // a deep link) — never on our own debounced write, which has already made the
  // two equal by the time this runs.
  React.useEffect(() => {
    setSearchTerm((current) => (current === urlQ ? current : urlQ));
  }, [urlQ]);

  const setParam = React.useCallback(
    (key: string, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          // A changed search invalidates the current page: page 3 of an
          // unfiltered list is not page 3 of a filtered one.
          if (key !== "page") next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  React.useEffect(() => {
    if (searchTerm === urlQ) return;
    const timer = setTimeout(() => setParam("q", searchTerm.trim()), ORG_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm, urlQ, setParam]);

  const { data, isPending, error } = useAdminOrgs(filters);
  const mapped = error ? toAdminOrgError(error) : null;
  const page = filters.page ?? 1;

  // Story 15.7 — the four stat tiles, mounted here BESIDE the directory list
  // rather than merged into it. A failure of this second read is deliberately
  // silent on this screen: the directory is the surface, the tiles are a summary
  // above it, and a banner about missing totals would push the table an operator
  // came for below the fold.
  const { data: totals } = useAdminOrgTotals();
  // The SERVER's date, so the per-row renewal band and the tile above it measure
  // the same 90 days from the same day zero (DW-426).
  const anchor = bandAnchor(totals);

  return (
    <section className="mx-auto flex w-full max-w-[var(--width-xl)] flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 id={HEADING_ID} className="display-xs text-[color:var(--text-primary)]">
            Organization directory
          </h1>
          <p className="text-sm text-[color:var(--text-secondary)]">
            Provision seat blocks and open an organization to review its contract,
            seat pool and operational contacts.
          </p>
        </div>
        {/* Sentence case, the house `Button` convention — the mockup's label reads
            "Add Organization". */}
        <Button asChild>
          <Link to="/admin/orgs/new">Add organization</Link>
        </Button>
      </div>

      {/* ONE control, not the mockup's `field-grid cols-3`: an empty three-column
          grid holding a single field is worse than a single field. */}
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField label="Search organization" htmlFor="admin-orgs-search">
          <Input
            id="admin-orgs-search"
            type="search"
            value={searchTerm}
            placeholder="Organization name or CRM key"
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </FormField>
      </div>

      {totals ? <StatsGrid stats={platformStats(totals)} /> : null}

      {mapped?.banner ? (
        <Banner variant={mapped.banner.variant} title={mapped.banner.title}>
          <p>{mapped.banner.body}</p>
          {mapped.banner.code ? (
            <p className="text-sm">{`Reference code: ${mapped.banner.code}`}</p>
          ) : null}
        </Banner>
      ) : null}

      {/* Loading renders NOTHING — there is no skeleton primitive in components/ui,
          and the sibling admin screens take the same posture. */}
      {!isPending && !error && data ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2
              id={LIST_HEADING_ID}
              className="text-lg font-semibold text-[color:var(--text-primary)]"
            >
              All organizations
            </h2>
            <p className="text-sm text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
              {`${data.count} ${data.count === 1 ? "organization" : "organizations"}`}
            </p>
          </div>

          {data.results.length === 0 ? (
            // Two DIFFERENT problems: one needs a different search, the other
            // needs the first organization to exist.
            filters.q ? (
              <Banner variant="info" title="No organizations match this search">
                <p>
                  Try the organization name, or the external reference ID supplied by
                  CITS.
                </p>
                <Button variant="secondary" size="sm" onClick={() => setSearchTerm("")}>
                  Clear search
                </Button>
              </Banner>
            ) : (
              <Banner variant="info" title="No organizations yet">
                <p>No organization has been provisioned on this platform yet.</p>
              </Banner>
            )
          ) : (
            <>
              <div className="overflow-x-auto rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
                <table
                  className="w-full border-collapse text-left text-sm"
                  aria-labelledby={LIST_HEADING_ID}
                >
                  <thead>
                    <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Organization
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Seats · used / capacity
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Org admins
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Calibration
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Next renewal
                      </th>
                      <th scope="col" className="px-4 py-3 font-semibold">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((row) => {
                      // BOTH pills are derived from this row's own payload. There
                      // is no server-side health enum to read, and none is added.
                      // Story 15.7 AC7a — the Status cell holds a LIST: the
                      // contract and the seat pool are independent axes and a row
                      // can be in trouble on both at once.
                      const statusBands = orgStatusPills(row, anchor);
                      const calibration = orgCalibrationPill(row);
                      return (
                        <tr
                          key={row.id}
                          className="border-b border-[color:var(--bg-quaternary)] align-middle last:border-b-0"
                        >
                          <td className="px-4 py-3">
                            <Link
                              to={`/admin/orgs/${encodeURIComponent(row.id)}`}
                              className="font-medium text-[color:var(--text-brand-secondary)] underline-offset-2 hover:underline"
                            >
                              {row.name}
                            </Link>
                            {/* The CRM key, the +1 delta vs. the mockup. An
                                absent key is the em-dash, never a blank line. */}
                            <div className="text-xs text-[color:var(--text-tertiary)]">
                              {row.external_reference_id ?? EMPTY_CELL}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
                            {/* GUARDED AS A CELL, not as a line. Story 21.4's
                                ledger entry reserved this for "the next story
                                that edits this cell", and that is this one:
                                `seat_consumed` and `seat_capacity` are typed
                                required and `strictNullChecks` is off anyway, so
                                nothing tells the compiler about an API deployed
                                before they existed (Rule 06 skew), and
                                the bare template rendered the literal
                                "undefined / undefined" to a platform operator —
                                four lines above a guard written against exactly
                                that failure. Same em-dash as the CRM sub-line. */}
                            {typeof row.seat_consumed === "number" &&
                            typeof row.seat_capacity === "number"
                              ? `${row.seat_consumed} / ${row.seat_capacity}`
                              : EMPTY_CELL}
                            {/* Story 21.4 (AC15) — the CARRIED ACTIVE HEADCOUNT,
                                beneath consumption and never instead of it. The
                                line above is the FR83 counter, which does not
                                fall when a seat is released, so an organization
                                that let three people go still reads 12 / 12
                                while nine people are working. A renewal is
                                floored at THIS number (ORG-022), so a director
                                reading only the line above cannot tell what the
                                renewal has to cover.

                                The name-cell sub-line above is the pattern this
                                copies — same size, same tertiary text. */}
                            <div className="text-xs text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
                              {/* Guarded for the same reason the CRM key above
                                  is, and by the same em-dash: an API deployed
                                  before this field existed omits it (Rule 06
                                  skew), and the unguarded template renders the
                                  literal "undefined active seats" to an
                                  operator. `active_seat_count` is now typed
                                  OPTIONAL — the recorded exception to the
                                  OPTIONALITY RULE in `admin-orgs-types.ts` — so
                                  the TYPE finally says what the wire does.
                                  It still does not enforce the guard: this
                                  project compiles with `strictNullChecks: false`
                                  (`tsconfig.app.json`), so every one of these is
                                  hand-written and held by a test, never by tsc.

                                  "active seats", not "active": the console's
                                  "Active clinicians" counts who can score
                                  today, and this figure is people holding a
                                  seat. Screen 24's tile beside it already says
                                  "Active seats", so both surfaces teach one
                                  phrase (PM ruling).

                                  The plural marker is the only thing that
                                  moves. The ruling is on the PHRASE, so this
                                  is a suffix rather than a second wording —
                                  the shape `AddPersonScreen.tsx` already uses
                                  for this same noun. n = 1 is reachable and
                                  not a boundary curiosity: the directory
                                  lists professional organizations and this is
                                  a live headcount, so any organization down
                                  to its last seat holder read the literal
                                  "1 active seats". n = 0 takes the plural and
                                  was already correct. */}
                              {typeof row.active_seat_count === "number"
                                ? `${row.active_seat_count} active seat${
                                    row.active_seat_count === 1 ? "" : "s"
                                  }`
                                : EMPTY_CELL}
                            </div>
                          </td>
                          <td className="px-4 py-3 [font-variant-numeric:tabular-nums]">
                            {/* The epic's cross-cutting constraint in one link:
                                "6 org admins" must reach six rows — with one
                                bounded exception (PM ruling 2026-09-05). An
                                owner the administration projection has no row
                                for is counted here and CANNOT be rendered by
                                the list, so this cell runs one above the rows
                                the link reaches. One id per organization, and
                                it closes only IF that row lands: on staging
                                2026-09-07 all fifteen unprojected accounts
                                were 17-45 days old, so in practice it does not
                                heal on its own. The repair is the projection
                                backfill (DW-534). */}
                            <Link
                              to={`/admin/users?org_id=${encodeURIComponent(row.id)}&role=org_admin`}
                              className="text-[color:var(--text-brand-secondary)] underline-offset-2 hover:underline"
                            >
                              {row.org_admin_count}
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={calibration.variant}>{calibration.label}</Badge>
                          </td>
                          <td className="px-4 py-3 text-[color:var(--text-secondary)] [font-variant-numeric:tabular-nums]">
                            {row.contract_end_date
                              ? formatDate(row.contract_end_date)
                              : EMPTY_CELL}
                          </td>
                          <td className="px-4 py-3">
                            {/* Wrapping rather than truncating: on a narrow
                                viewport the second band must still be readable,
                                since hiding it is precisely what AC7a removed. */}
                            <div className="flex flex-wrap gap-1">
                              {statusBands.map((band) => (
                                <Badge key={band.label} variant={band.variant}>
                                  {band.label}
                                </Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Page-based navigation, with the server's `previous`/`next` URLs
                  as the authority on whether a neighbouring page EXISTS. The URLs
                  themselves are not followed — the search term already lives in
                  the params, so bumping `page` preserves it. */}
              {data.previous || data.next ? (
                <nav
                  className="flex items-center justify-between gap-4"
                  aria-label="Organization list pagination"
                >
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!data.previous}
                    onClick={() => setParam("page", String(page - 1))}
                  >
                    Previous
                  </Button>
                  <p className="text-sm text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
                    {`Page ${page}`}
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!data.next}
                    onClick={() => setParam("page", String(page + 1))}
                  >
                    Next
                  </Button>
                </nav>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}

export default OrgDirectoryScreen;
